import { useState } from "react";
import { Copy } from "lucide-react";

export const MONTH_ABBR = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
export const MONTH_FULL = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

export function ChoicePill({ active, onClick, Icon, label, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={`inline-flex items-center justify-center gap-2 px-3 py-2.5 text-xs uppercase tracking-[0.16em] border transition-colors ${
        active
          ? "bg-[hsl(var(--accent))] text-[#1A201F] border-[hsl(var(--accent))]"
          : "bg-transparent text-white/80 border-white/30 hover:border-white/60"
      }`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

export function BillField({ label, value, onChange, testid, required }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-white/75 mb-1.5">
        {label}{required ? " *" : ""}
      </label>
      <input
        type="text" value={value || ""} onChange={(e) => onChange(e.target.value)}
        required={!!required}
        className="w-full bg-white/10 border border-white/30 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[hsl(var(--accent))]"
        data-testid={testid}
      />
    </div>
  );
}

export function BankRow({ label, value, copyKey, copy, copiedKey }) {
  return (
    <div className="grid grid-cols-[110px_1fr_auto] items-center gap-3 py-1.5 border-b border-white/10 last:border-0">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">{label}</div>
      <div className="font-mono text-[12.5px] text-white break-all">{value}</div>
      <button
        type="button"
        onClick={() => copy(value, copyKey)}
        className="text-[10px] uppercase tracking-[0.16em] text-white/70 hover:text-[hsl(var(--accent))] flex items-center gap-1"
        data-testid={`bank-copy-${copyKey}`}
      >
        <Copy className="h-3 w-3" /> {copiedKey === copyKey ? "✓" : ""}
      </button>
    </div>
  );
}

// SEPA bank transfer details block with per-row copy buttons.
export function BankTransferBlock({ bankDetails, reference, isMonthlyView, monthlyPrice }) {
  const [copiedKey, setCopiedKey] = useState("");
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(""), 2000);
    } catch (e) {
      console.warn("[QuotaCard] clipboard copy failed:", e?.message);
    }
  };
  return (
    <div className="mt-4 bg-white/8 backdrop-blur-sm border border-white/15 p-5 text-[13px] leading-relaxed" data-testid="member-bank-block">
      <div className="overline text-white/80 mb-2">Transferência bancária · SEPA</div>
      <BankRow label="IBAN" value={bankDetails.iban} copyKey="iban" copy={copy} copiedKey={copiedKey} />
      {bankDetails.bic && bankDetails.bic !== "—" && (
        <BankRow label="BIC/SWIFT" value={bankDetails.bic} copyKey="bic" copy={copy} copiedKey={copiedKey} />
      )}
      <BankRow label="Beneficiário" value={bankDetails.beneficiary} copyKey="benef" copy={copy} copiedKey={copiedKey} />
      <BankRow label="Referência" value={reference} copyKey="ref" copy={copy} copiedKey={copiedKey} />
      {isMonthlyView && (
        <p className="mt-2 text-[11px] text-white/65 leading-relaxed">
          Dica: pode criar no seu banco uma <strong>ordem permanente</strong> de {Number(monthlyPrice).toFixed(2)} €/mês para este IBAN.
        </p>
      )}
      <p className="mt-3 text-[11px] text-white/65 leading-relaxed">
        Após transferir, envie um email para <a href="mailto:geral@ciees.pt" className="underline">geral@ciees.pt</a> com o comprovativo.
        Vamos confirmar manualmente e enviar-lhe a Declaração de Donativo em PDF.
      </p>
    </div>
  );
}
